/**
 * Cognito Utilities
 *
 * Helper functions for working with AWS Cognito user data and JWT claims.
 */
/**
 * Parse Cognito groups from JWT token claims.
 *
 * Cognito groups can be provided in different formats depending on the context:
 * - As an array of strings (most common in JWT tokens)
 * - As a comma-separated string (some legacy implementations)
 *
 * This function normalizes both formats into a consistent array of strings.
 *
 * @param rawGroups - The cognito:groups claim value from JWT token
 * @returns Array of group names, or empty array if no groups
 *
 * @example
 * ```typescript
 * // From JWT token claims
 * const groups = parseCognitoGroups(claims?.['cognito:groups']);
 * // => ['admin', 'user']
 *
 * // Handle array format
 * parseCognitoGroups(['admin', 'user']); // => ['admin', 'user']
 *
 * // Handle string format
 * parseCognitoGroups('admin,user'); // => ['admin', 'user']
 *
 * // Handle undefined/null
 * parseCognitoGroups(undefined); // => []
 * ```
 */
export function parseCognitoGroups(rawGroups) {
    if (!rawGroups) {
        return [];
    }
    if (Array.isArray(rawGroups)) {
        return rawGroups;
    }
    if (typeof rawGroups === 'string') {
        return rawGroups
            .split(',')
            .map((g) => g.trim())
            .filter((g) => g.length > 0);
    }
    return [];
}
/**
 * Filter and map Cognito groups to validated user roles.
 *
 * Cognito groups use plural names (admins, coaches, users) per AWS conventions.
 * Application roles use singular names (admin, coach, user) for the enum type.
 *
 * @param groups - Array of Cognito group names (plural)
 * @returns Array of validated CognitoRole values (singular)
 *
 * @example
 * ```typescript
 * filterUserRoles(['admins', 'users']) // => ['admin', 'user']
 * filterUserRoles(['coaches']) // => ['coach']
 * filterUserRoles(['other-group']) // => []
 * ```
 */
export function filterUserRoles(groups) {
    const roleMap = {
        admins: 'admin',
        coaches: 'coach',
        users: 'user',
    };
    return groups
        .map((g) => roleMap[g])
        .filter((role) => role !== undefined);
}
/**
 * Parse and validate Cognito groups from JWT claims into CognitoRole array.
 *
 * Combines parsing and filtering into a single convenience function.
 *
 * @param rawGroups - The cognito:groups claim value from JWT token
 * @returns Array of validated CognitoRole values
 *
 * @example
 * ```typescript
 * const roles = parseCognitoRoles(claims?.['cognito:groups']);
 * // => ['admin', 'user']
 * ```
 */
export function parseCognitoRoles(rawGroups) {
    const groups = parseCognitoGroups(rawGroups);
    return filterUserRoles(groups);
}
//# sourceMappingURL=cognito.js.map